import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ROUTES } from '../config/constants'
import AppMockup from './AppMockup'

export default function HeroNew() {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-start overflow-hidden pt-28 md:pt-32 pb-16 bg-grid-pattern">
      {/* Animated background orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.08, 0.15, 0.08],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-pitch-green rounded-full blur-[150px]"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.06, 0.12, 0.06],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gold rounded-full blur-[150px]"
        />
        <motion.div
          animate={{
            scale: [1.1, 1, 1.1],
            opacity: [0.04, 0.08, 0.04],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500 rounded-full blur-[200px]"
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6"
        >
          <a
            href="https://www.instagram.com/reelcricket.in/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-pink-500/30 bg-pink-500/10 hover:bg-pink-500/20 backdrop-blur-sm transition-all transform hover:scale-105 cursor-pointer text-pink-400 hover:text-pink-300 font-bold"
          >
            <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
            <span className="text-xs tracking-wide flex items-center gap-1">
              ★ Proof of Work — See Our Instagram Portfolio ↗
            </span>
          </a>

          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-pitch-green/30 bg-pitch-green/10 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-pitch-green animate-pulse" />
            <span className="text-xs font-semibold text-pitch-green tracking-wide">🖥️ Free Desktop Tool — Windows &amp; Mac</span>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-5xl md:text-7xl lg:text-8xl font-black text-center leading-[0.95] mb-6"
        >
          <span className="text-white">Your Best Cricket Shots,</span>
          <br />
          <span className="gradient-text">Ready to Go Viral.</span>
        </motion.h1>

        {/* Sub-headline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-lg md:text-xl text-gray-400 text-center max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          We turn your raw match footage into{' '}
          <span className="text-gray-200 font-semibold">player-specific highlight reels</span>
          {' '}— delivered in{' '}
          <span className="text-pitch-green font-semibold">24–72 hours</span>.
          Upload once. Share everywhere.
        </motion.p>

        {/* Dual CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14"
        >
          <Link
            to={ROUTES.UPLOAD}
            id="hero-upload-cta"
            className="group relative inline-flex items-center gap-2 bg-pitch-green text-stadium-black px-8 py-4 rounded-full text-lg font-bold hover:bg-neon-green transition-all transform hover:scale-105 shadow-lg shadow-pitch-green/30"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Get My Highlight Reel →
          </Link>

          <Link
            to={ROUTES.DOWNLOAD}
            id="hero-download-cta"
            className="group relative inline-flex items-center gap-2 px-8 py-4 rounded-full text-lg font-bold border-2 border-white/20 text-white hover:border-pitch-green/50 hover:text-pitch-green transition-all transform hover:scale-105 backdrop-blur-sm"
          >
            <svg className="w-5 h-5 transition-transform group-hover:-translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Edit Reels Myself — Free
          </Link>
        </motion.div>

        {/* Guarantee Strip */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gray-500 mt-4"
        >
          <span className="flex items-center gap-1.5">
            <span className="text-pitch-green">✅</span> 24–72 hr delivery guaranteed
          </span>
          <span className="text-gray-700 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <span className="text-pitch-green">🔒</span> 100% satisfaction or we re-edit free
          </span>
          <span className="text-gray-700 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <span className="text-pitch-green">📱</span> Ready for Instagram, Shorts &amp; WhatsApp
          </span>
        </motion.p>

        {/* App Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="max-w-5xl mx-auto perspective-tilt"
        >
          <AppMockup />
        </motion.div>

        {/* Trust Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mt-10"
        >
          {[
            { icon: '🏏', text: '1,000+ Reels Delivered' },
            { icon: '⚡', text: '24–72 hr Turnaround' },
            { icon: '📱', text: 'Instagram, Shorts & WhatsApp' },
            { icon: '🎬', text: 'IPL-Style Cinematic Edits' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors">
              <span className="text-base">{item.icon}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
